import { useEffect } from "react";
import NotificationPrompt from "@/components/NotificationPrompt";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Shell from "@/components/layout/Shell";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import MitraDashboard from "@/pages/MitraDashboard";
import Browse from "@/pages/Browse";
import ProfileDetail from "@/pages/ProfileDetail";
import Profile from "@/pages/Profile";
import Interests from "@/pages/Interests";
import Shortlist from "@/pages/Shortlist";
import Chat from "@/pages/Chat";
import Kundli from "@/pages/Kundli";
import BiodataBuilder from "@/pages/BiodataBuilder";
import Premium from "@/pages/Premium";
import Viewers from "@/pages/Viewers";
import Privacy from "@/pages/Privacy";
import AdminLayout, { AdminProtected } from "@/components/admin/AdminLayout";
import AdminOverview from "@/pages/admin/Overview";
import AdminUsers from "@/pages/admin/Users";
import AdminLogin from "@/pages/admin/Login";
import AdminVerifications from "@/pages/admin/Verifications";
import AdminReports from "@/pages/admin/Reports";
import AdminTickets from "@/pages/admin/Tickets";
import AdminHomeCMS from "@/pages/admin/HomeCMS";
import AdminPackages from "@/pages/admin/Packages";
import AdminSubscriptions from "@/pages/admin/Subscriptions";
import AdminReferrals from "@/pages/admin/Referrals";
import { SettingsPayments, SettingsSMS, SettingsEmail, SettingsCaptcha, SettingsPush, SettingsNotifications, SettingsGoogle, SettingsCore } from "@/pages/admin/SettingsPages";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Verify from "@/pages/Verify";
import PhoneLogin from "@/pages/PhoneLogin";
import GoogleCallback from "@/pages/GoogleCallback";
import VerifyOtp from "@/pages/VerifyOtp";
import About from "@/pages/static/About";
import Terms from "@/pages/static/Terms";
import PrivacyPolicy from "@/pages/static/PrivacyPolicy";
import CookiePolicy from "@/pages/static/CookiePolicy";
import Grievances from "@/pages/static/Grievances";
import SafetyTips from "@/pages/static/SafetyTips";
import Contact from "@/pages/static/Contact";
import FAQs from "@/pages/static/FAQs";
import SuccessStories from "@/pages/static/SuccessStories";
import Sitemap from "@/pages/static/Sitemap";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  
  const isStaff = user.role === "admin" || user.role === "mitra" || user.is_mitra || user.is_impersonated;
  if (!isStaff && !user.profile_complete && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

// Mitra-only route: redirects non-mitra to regular /app
function MitraProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_mitra) return <Navigate to="/app" replace />;
  return children;
}

// Dashboard that auto-redirects Mitra users to /mitra
function SmartDashboard() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  if (user?.is_mitra) return <Navigate to="/mitra" replace />;
  return <Dashboard />;
}

function AppShellWrap({ children }) {
  return <Shell>{children}</Shell>;
}

// Global notification prompt — reads auth, shows popup when permission not yet decided
function GlobalNotificationPrompt() {
  const { user } = useAuth();
  return <NotificationPrompt user={user} />;
}

import { LanguageProvider } from "@/context/LanguageContext";

function App() {
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      window._pwaInstallPrompt = e;
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) {
      localStorage.setItem("jj_referral_code", ref.toUpperCase().trim());
    }
  }, []);

  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/login/phone" element={<PhoneLogin />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/user/social-login/callback/google" element={<GoogleCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-otp" element={<VerifyOtp />} />
            <Route path="/about" element={<About />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/grievances" element={<Grievances />} />
            <Route path="/safety-tips" element={<SafetyTips />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faqs" element={<FAQs />} />
            <Route path="/success-stories" element={<SuccessStories />} />
            <Route path="/sitemap" element={<Sitemap />} />
            <Route path="/biodata" element={<AppShellWrap><BiodataBuilder /></AppShellWrap>} />
            <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
            <Route path="/app" element={<Protected><AppShellWrap><SmartDashboard /></AppShellWrap></Protected>} />
            <Route path="/mitra" element={<MitraProtected><MitraDashboard /></MitraProtected>} />
            <Route path="/app/browse" element={<Protected><AppShellWrap><Browse /></AppShellWrap></Protected>} />
            <Route path="/app/profile" element={<Protected><AppShellWrap><Profile /></AppShellWrap></Protected>} />
            <Route path="/app/profile/:id" element={<Protected><AppShellWrap><ProfileDetail /></AppShellWrap></Protected>} />
            <Route path="/app/interests" element={<Protected><AppShellWrap><Interests /></AppShellWrap></Protected>} />
            <Route path="/app/shortlist" element={<Protected><AppShellWrap><Shortlist /></AppShellWrap></Protected>} />
            <Route path="/app/chat" element={<Protected><AppShellWrap><Chat /></AppShellWrap></Protected>} />
            <Route path="/app/chat/:id" element={<Protected><AppShellWrap><Chat /></AppShellWrap></Protected>} />
            <Route path="/app/kundli" element={<Protected><AppShellWrap><Kundli /></AppShellWrap></Protected>} />
            <Route path="/app/biodata" element={<AppShellWrap><BiodataBuilder /></AppShellWrap>} />
            <Route path="/app/viewers" element={<Protected><AppShellWrap><Viewers /></AppShellWrap></Protected>} />
            <Route path="/app/verify" element={<Protected><AppShellWrap><Verify /></AppShellWrap></Protected>} />
            <Route path="/app/premium" element={<Protected><AppShellWrap><Premium /></AppShellWrap></Protected>} />
            <Route path="/app/privacy" element={<Protected><AppShellWrap><Privacy /></AppShellWrap></Protected>} />

            <Route path="/admin" element={<AdminProtected><AdminLayout /></AdminProtected>}>
              <Route index element={<AdminOverview />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="tickets" element={<AdminTickets />} />
              <Route path="verifications" element={<AdminVerifications />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="cms" element={<AdminHomeCMS />} />
              <Route path="packages" element={<AdminPackages />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="referrals" element={<AdminReferrals />} />
              <Route path="settings/payments" element={<SettingsPayments />} />
              <Route path="settings/sms" element={<SettingsSMS />} />
              <Route path="settings/email" element={<SettingsEmail />} />
              <Route path="settings/captcha" element={<SettingsCaptcha />} />
              <Route path="settings/push" element={<SettingsPush />} />
              <Route path="settings/notifications" element={<SettingsNotifications />} />
              <Route path="settings/google" element={<SettingsGoogle />} />
              <Route path="settings/core" element={<SettingsCore />} />
            </Route>
            <Route path="/app/admin" element={<Navigate to="/admin" replace />} />
            <Route path="/app/admin/settings" element={<Navigate to="/admin/settings/sms" replace />} />
            <Route path="/app/admin/verifications" element={<Navigate to="/admin/verifications" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <GlobalNotificationPrompt />
          <Toaster position="top-center" richColors />
          <PWAInstallBanner />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
